// @ts-check
// Abas "Configurações" e "Saída" do editor, abertas no painel lateral (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de
// fluxos, seção 17). Configurações: fins de semana nas esperas, quantas vezes o mesmo lead percorre, o responsável pelo
// fluxo, coleção e descrição. Saída: as condições em que o lead sai (cada uma uma caixa, a do fim sempre marcada), a
// linha fixa de "Não é lead ou excluído" e o lembrete de que quem sai não recebe as próximas ações.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Checkbox } from '../../ui/feedback.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { AudienceBuilder } from '../audiences/audience-builder.js';
import { MAXIMUM_FLOW_COLLECTION_LENGTH, MAXIMUM_FLOW_DESCRIPTION_LENGTH } from '../../../../shared/flows/flow-limits.js';
import { FLOW_REPLY_CHANNELS } from '../../../../shared/flows/flow-definition.js';
import { stageOptions } from './flow-lookups.js';
import { FlowRadioOption } from './flow-lifecycle-dialogs.js';
import { FlowSidePanel } from './flow-side-panel.js';
import { FlowField, FlowNotice, flowProblemText } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowExitCriterion} FlowExitCriterion
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 */

/**
 * @param {{ settings: FlowDefinition['settings'], owner: { id: string, name: string } | null, collection: string | null, description: string | null,
 *   collections: string[], problems: FlowProblem[], onChange: (settings: FlowDefinition['settings']) => void,
 *   onDetailsChange: (patch: { collection?: string | null, description?: string | null }) => void, onClose: () => void }} props
 */
export function FlowSettingsPanel({ settings, owner, collection, description, collections, problems, onChange, onDetailsChange, onClose }) {
  const [collectionDraft, setCollectionDraft] = useState(collection ?? '');
  const [descriptionDraft, setDescriptionDraft] = useState(description ?? '');
  useEffect(() => setCollectionDraft(collection ?? ''), [collection]);
  useEffect(() => setDescriptionDraft(description ?? ''), [description]);
  const settingsProblems = problems.filter((problem) => problem.area === 'SETTINGS');
  return html`<${FlowSidePanel} title=${t('flows.settings.title')} help=${t('flows.settings.help')} onClose=${onClose} panelKey="settings" testId="flow-settings-panel">
    ${settingsProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
    <section class="flow-panel-section">
      <label class="flow-checkbox-row flow-checkbox-row-top">
        <${Checkbox} checked=${settings.considerWeekends !== false} ariaLabel=${t('flows.settings.weekends')}
          onChange=${(/** @type {boolean} */ considerWeekends) => onChange({ ...settings, considerWeekends })} />
        <span class="flow-checkbox-texts">
          <span class="flow-radio-title">${t('flows.settings.weekends')}</span>
          <span class="flow-radio-description">${t('flows.settings.weekendsHint')}</span>
        </span>
      </label>
    </section>
    <section class="flow-panel-section">
      <h3 class="flow-panel-section-title">${t('flows.settings.reentry')}</h3>
      <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.settings.reentry')}>
        <${FlowRadioOption} name="flow-reentry" value="MULTIPLE" checked=${settings.reentry === 'MULTIPLE'} title=${t('flows.settings.reentryMultiple')}
          description=${t('flows.settings.reentryMultipleHint')} onSelect=${() => onChange({ ...settings, reentry: 'MULTIPLE' })} />
        <${FlowRadioOption} name="flow-reentry" value="ONCE" checked=${settings.reentry !== 'MULTIPLE'} title=${t('flows.settings.reentryOnce')}
          description=${t('flows.settings.reentryOnceHint')} onSelect=${() => onChange({ ...settings, reentry: 'ONCE' })} />
      </div>
    </section>
    <section class="flow-panel-section">
      <h3 class="flow-panel-section-title">${t('flows.settings.owner')}</h3>
      <p class="flow-owner-name"><${Icon} name="IconUserCircle" size=${16} /><span>${owner?.name ?? t('flows.settings.noOwner')}</span></p>
      <p class="flow-field-hint">${t('flows.settings.ownerHint')}</p>
    </section>
    <section class="flow-panel-section">
      <${FlowField} label=${t('flows.nameDialog.collection')} htmlFor="flow-settings-collection" hint=${t('flows.nameDialog.collectionHint')}>
        <input id="flow-settings-collection" class="text-input" value=${collectionDraft} maxLength=${MAXIMUM_FLOW_COLLECTION_LENGTH} list="flow-settings-collections"
          onInput=${(/** @type {InputEvent} */ event) => setCollectionDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
          onBlur=${() => collectionDraft.trim() !== (collection ?? '') && onDetailsChange({ collection: collectionDraft.trim() || null })} />
        <datalist id="flow-settings-collections">${collections.map((value) => html`<option key=${value} value=${value} />`)}</datalist>
      <//>
      <${FlowField} label=${t('flows.settings.description')} htmlFor="flow-settings-description">
        <textarea id="flow-settings-description" class="text-input" rows="3" maxLength=${MAXIMUM_FLOW_DESCRIPTION_LENGTH} value=${descriptionDraft}
          onInput=${(/** @type {InputEvent} */ event) => setDescriptionDraft(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
          onBlur=${() => descriptionDraft.trim() !== (description ?? '') && onDetailsChange({ description: descriptionDraft.trim() || null })}></textarea>
      <//>
    </section>
  <//>`;
}

const SHORTCUT_STAGES = Object.freeze(['ENROLLED', 'LOST']);

/**
 * @param {{ criteria: FlowExitCriterion[], problems: FlowProblem[], onChange: (criteria: FlowExitCriterion[]) => void, onClose: () => void }} props
 */
export function FlowExitPanel({ criteria, problems, onChange, onClose }) {
  /** @param {string} kind */
  const find = (kind) => /** @type {any} */ (criteria.find((criterion) => criterion.kind === kind) ?? null);
  /** @param {string} kind @param {boolean} enabled @param {() => FlowExitCriterion} create */
  const toggle = (kind, enabled, create) => onChange(enabled ? [...criteria, create()] : criteria.filter((criterion) => criterion.kind !== kind));
  /** @param {string} kind @param {Record<string, unknown>} patch */
  const update = (kind, patch) => onChange(criteria.map((criterion) => (criterion.kind === kind ? /** @type {FlowExitCriterion} */ ({ ...criterion, ...patch }) : criterion)));
  const stageReached = find('STAGE_REACHED');
  const audience = find('AUDIENCE');
  const replied = find('CONTACT_REPLIED');
  /** @param {string} kind */
  const criterionProblems = (kind) => problems.filter((problem) => problem.criterionId && problem.criterionId === find(kind)?.id);
  const generalProblems = problems.filter((problem) => problem.area === 'EXIT' && !problem.criterionId);
  const stages = stageOptions();
  /** @param {string} kind @param {string} label @param {() => FlowExitCriterion} create @param {any} [children] */
  const row = (kind, label, create, children = null) => {
    const isOn = Boolean(find(kind));
    const rowProblems = criterionProblems(kind);
    return html`<li class="flow-exit-row" data-invalid=${rowProblems.length > 0 ? 'true' : undefined}>
      <label class="flow-checkbox-row">
        <${Checkbox} checked=${isOn} ariaLabel=${label} onChange=${(/** @type {boolean} */ value) => toggle(kind, value, create)} />
        <span>${label}</span>
      </label>
      ${isOn && children ? html`<div class="flow-exit-details">${children}</div>` : null}
      ${rowProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
    </li>`;
  };
  return html`<${FlowSidePanel} title=${t('flows.exit.title')} help=${t('flows.exit.help')} onClose=${onClose} panelKey="exit" testId="flow-exit-panel">
    <p class="flow-panel-lead">${t('flows.exit.lead')}</p>
    ${generalProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
    <ul class="flow-exit-list">
      <li class="flow-exit-row">
        <label class="flow-checkbox-row" data-disabled="true">
          <${Checkbox} checked=${true} disabled=${true} ariaLabel=${t('flows.exit.end')} />
          <span>${t('flows.exit.end')}</span>
        </label>
      </li>
      ${row('STAGE_REACHED', t('flows.exit.stageReached'), () => ({ id: crypto.randomUUID(), kind: 'STAGE_REACHED', stages: ['ENROLLED', 'LOST'] }), stageReached
        ? html`<div class="flow-exit-shortcuts">
            ${SHORTCUT_STAGES.map((value) => {
              const option = stages.find((candidate) => candidate.value === value);
              const isChecked = stageReached.stages.includes(value);
              return option
                ? html`<button type="button" key=${value} class="flow-tag-option" aria-pressed=${isChecked ? 'true' : 'false'}
                    onClick=${() => update('STAGE_REACHED', { stages: isChecked ? stageReached.stages.filter((stage) => stage !== value) : [...stageReached.stages, value] })}>
                    <${Tag} color=${option.color ?? 'gray'} text=${option.label} />
                  </button>`
                : null;
            })}
          </div>
          <details class="flow-exit-more-stages">
            <summary>${t('flows.exit.otherStages')}</summary>
            <div class="flow-tag-options">
              ${stages.filter((option) => !SHORTCUT_STAGES.includes(option.value)).map((option) => {
                const isChecked = stageReached.stages.includes(option.value);
                return html`<button type="button" key=${option.value} class="flow-tag-option" aria-pressed=${isChecked ? 'true' : 'false'}
                  onClick=${() => update('STAGE_REACHED', { stages: isChecked ? stageReached.stages.filter((stage) => stage !== option.value) : [...stageReached.stages, option.value] })}>
                  <${Tag} color=${option.color ?? 'gray'} text=${option.label} />
                </button>`;
              })}
            </div>
          </details>`
        : null)}
      ${row('STAGE_CHANGED', t('flows.exit.stageChanged'), () => ({ id: crypto.randomUUID(), kind: 'STAGE_CHANGED' }))}
      ${row('AUDIENCE', t('flows.exit.audience'), () => ({ id: crypto.randomUUID(), kind: 'AUDIENCE', audience: null }), audience
        ? html`<${AudienceBuilder} key=${audience.id} value=${audience.audience} usage="CONDITION" channels=${[]} showRecipients=${false}
            onChange=${(/** @type {any} */ value) => update('AUDIENCE', { audience: value })} />`
        : null)}
      ${row('MARKETING_OPT_OUT', t('flows.exit.optOut'), () => ({ id: crypto.randomUUID(), kind: 'MARKETING_OPT_OUT' }))}
      ${row('CONTACT_REPLIED', t('flows.exit.replied'), () => ({ id: crypto.randomUUID(), kind: 'CONTACT_REPLIED', channels: ['WHATSAPP', 'EMAIL'] }), replied
        ? html`<div class="flow-exit-channels">
            ${FLOW_REPLY_CHANNELS.map((channel) => html`<label key=${channel} class="flow-checkbox-row">
              <${Checkbox} checked=${replied.channels.includes(channel)} ariaLabel=${t(`flows.list.channels.${channel}`)}
                onChange=${(/** @type {boolean} */ value) => update('CONTACT_REPLIED', { channels: value ? [...replied.channels, channel] : replied.channels.filter((item) => item !== channel) })} />
              <span>${t(`flows.list.channels.${channel}`)}</span>
            </label>`)}
          </div>`
        : null)}
    </ul>
    <p class="flow-exit-always"><${Icon} name="IconLock" size=${14} /><span>${t('flows.exit.always')}</span></p>
    <div class="flow-exit-reminder"><${FlowNotice} icon="IconBulb" tone="tip">${t('flows.exit.reminder')}<//></div>
  <//>`;
}
